import React from 'react';

import { Button, Stack } from '@mui/material';

import { Field, Form } from '@hrlogics/react-components/form';

import { EmployeeSearchInputKindEnum } from 'src/api';
import { TextField } from 'src/components/finalForm';
import type { EmployeeSearchCriteria } from 'src/types/employeeSearch';

import { EmployeeIdSearchFormFields } from './constants';
import type { EmployeeIdSearchFormValues } from './types';
import { employeeIdSearchFormValidationSchema } from './validation';

type EmployeeIdSearchFormProps = {
  onSubmit: (criteria: EmployeeSearchCriteria) => void;
  loading?: boolean;
  /** Search restored from the URL; prefills the form when it's an employee id search. */
  initialCriteria?: EmployeeSearchCriteria | null;
};

export const EmployeeIdSearchForm: React.FC<EmployeeIdSearchFormProps> = ({
  onSubmit,
  loading,
  initialCriteria,
}) => {
  const handleSubmit = React.useCallback(
    (values: EmployeeIdSearchFormValues) =>
      onSubmit({
        kind: EmployeeSearchInputKindEnum.EmployeeId,
        extEmployeeId: values.employeeId?.trim() || undefined,
      }),
    [onSubmit],
  );

  // Held in state so the reference stays stable — `Form` reinitializes on a new one.
  const [initialValues] = React.useState<Partial<EmployeeIdSearchFormValues>>(() =>
    initialCriteria?.kind === EmployeeSearchInputKindEnum.EmployeeId
      ? { [EmployeeIdSearchFormFields.employeeId]: initialCriteria.extEmployeeId ?? undefined }
      : EMPLOYEE_ID_INITIAL_VALUES,
  );

  return (
    <Form<EmployeeIdSearchFormValues>
      onSubmit={handleSubmit}
      initialValues={initialValues}
      validationSchema={employeeIdSearchFormValidationSchema}
      subscription={{ submitting: true, valid: true, dirty: true }}
      containerProps={{ mt: 0 }}
    >
      {({ submitting, valid, dirty }) => (
        <React.Fragment>
          <Field
            name={EmployeeIdSearchFormFields.employeeId}
            component={TextField}
            label="Employee ID"
            placeholder="Employee ID"
            disabled={submitting || loading}
            fullWidth
          />

          <Stack direction="row" justifyContent="flex-end" mt={3}>
            <Button
              type="submit"
              variant="contained"
              disabled={!valid || submitting || loading || !dirty}
              loading={submitting || loading}
            >
              Run search
            </Button>
          </Stack>
        </React.Fragment>
      )}
    </Form>
  );
};

const EMPLOYEE_ID_INITIAL_VALUES: Partial<EmployeeIdSearchFormValues> = {
  [EmployeeIdSearchFormFields.employeeId]: undefined,
};
